import React, {useEffect} from "react";
import {IntlProvider} from "react-intl";
import {useSelector} from "react-redux";
import {Router} from "react-router-dom";

import MomentUtils from "@date-io/moment";
import {createBrowserHistory} from "history";
import {create} from "jss";
import rtl from "jss-rtl";
import {SnackbarProvider} from "notistack";

import {
  StylesProvider,
  ThemeProvider,
  createStyles,
  jssPreset,
  makeStyles,
  useTheme
} from "@material-ui/core";
import * as locales from "@material-ui/core/locale";
import {MuiPickersUtilsProvider} from "@material-ui/pickers";
import Auth from "src/components/Auth";
// import CookiesNotification from 'src/components/CookiesNotification';
// import SettingsNotification from 'src/components/SettingsNotification';
import GoogleAnalytics from "src/components/GoogleAnalytics";
import ScrollReset from "src/components/ScrollReset";

import useSettings from "src/hooks/useSettings";

import {DialogPortal} from "./components/ComfirmAsync/DialogWrapper";
import {LOCALES} from "./i18n/locales";
import {createCustomTheme} from "./theme";
import Routes from "src/Routes";
import English from "src/i18n/lang/en.json";
import Vietnamese from "src/i18n/lang/vi.json";

const history = createBrowserHistory();
const jss = create({plugins: [...jssPreset().plugins, rtl()]});

const useStyles = makeStyles(() =>
  createStyles({
    "@global": {
      "*": {
        boxSizing: "border-box",
        margin: 0,
        padding: 0,
        fontFamily: "roboto"
      },
      ".ant-select-dropdown": {
        zIndex: "2000 !important"
      },
      html: {
        "-webkit-font-smoothing": "antialiased",
        "-moz-osx-font-smoothing": "grayscale",
        height: "100%",
        width: "100%"
      },
      body: {
        height: "100%",
        width: "100%"
      },
      "#root": {
        height: "100%",
        width: "100%"
      },
      ".tox-fullscreen": {
        zIndex: "1301 !important"
      },
      ".flex-center": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      ".MuiFormHelperText-contained": {
        margin: 0
      }
    }
  })
);

function App() {
  console.warn = () => null;
  useStyles();
  const {settings} = useSettings();
  const {currentLanguage} = useSelector((state) => state.multiLanguage);

  const switchLangResource = (langCode) => {
    switch (langCode) {
      case LOCALES.ENGLISH.code:
        return English;
      case LOCALES.VIETNAMESE.code:
        return Vietnamese;
      default:
        return Vietnamese;
    }
  };

  const theme = useTheme();

  const themeWithLocale = React.useMemo(() => {
    settings.locale =
      locales[currentLanguage?.code?.replace("-", "") || LOCALES.ENGLISH.code];
    return createCustomTheme(settings);
  }, [currentLanguage, theme]);

  useEffect(() => {
    if (currentLanguage) {
      localStorage.setItem("languageInUse", JSON.stringify(currentLanguage));
    }
  }, [currentLanguage]);

  return (
    <IntlProvider
      messages={switchLangResource(currentLanguage?.code)}
      locale={currentLanguage?.code}
      defaultLocale={LOCALES.VIETNAMESE.code}
    >
      <ThemeProvider key={settings.locale} theme={themeWithLocale}>
        <StylesProvider jss={jss}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <SnackbarProvider maxSnack={2}>
              <DialogPortal>
                <Router history={history}>
                  <Auth>
                    <ScrollReset />
                    <GoogleAnalytics />
                    {/* <CookiesNotification /> */}
                    {/* <SettingsNotification /> */}
                    <Routes />
                  </Auth>
                </Router>
              </DialogPortal>
            </SnackbarProvider>
          </MuiPickersUtilsProvider>
        </StylesProvider>
      </ThemeProvider>
    </IntlProvider>
  );
}

export default App;
